import React, { type FC, type JSX, useEffect, useState } from 'react';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { createMyPriceLoginPopupClickedPayload } from '@/app/common/starTrack/events/myPriceLoginPopupClicked';
import { ModalBody, ModalButtons, ModalImage, ModalPrimaryButton, ModalSecondaryButton } from './styled';
import clickEvent from '../../../../Curiosity/clickEvent';
import { RouteType } from '../../../../Curiosity';
import { getCookie, setCookie } from '../../../../utils/Cookies/clientCookies';
import { COOKIE_KEY } from '../../../../utils/Cookies/types';
import OpenXModal from '../../OpenXModal';
import getSearchParams from '../../../../common/getSearchParams.ts';

type MypriceProps = {
  loggedIn?: boolean;
  t: TranslationFunction;
};

const MyPriceModal: FC<MypriceProps> = ({ loggedIn, t }): JSX.Element => {
  const { commonData, localization, curiosityParams } = usePageWrapperData();
  const { starTrack, isMobile } = useCommonClient();
  const { imageBasePath } = localization;
  const [showMypriceModal, setShowMypriceModal] = useState<boolean>(false);
  const image = `${imageBasePath}icons/myprice_icon.svg`;
  const expireDays = 1;
  const hasCookie = !!getCookie(COOKIE_KEY.MYPRICE_POPUP_SHOWN);
  const primary = t('vacation.oss.myprice.modal.primary');
  const secondary = t('vacation.oss.myprice.modal.secondary');
  const description = t('vacation.oss.myprice.modal.desc');

  useEffect(() => {
    if (loggedIn) {
      setCookie(COOKIE_KEY.MYPRICE_POPUP_SHOWN, 'true', 14);
    } else if (!hasCookie) {
      handleShowModal();
    }
  }, []);

  const handleShowModal = () => {
    clickEvent({
      clickParams: {
        event_name: 'myprice_modal',
        event_context: 'popup_show',
      },
      routeType: RouteType.OSMAP,
      commonData,
      localization,
      curiosityParams,
    });
    setShowMypriceModal(true);
    setCookie(COOKIE_KEY.MYPRICE_POPUP_SHOWN, 'true', expireDays);
  };

  const closeModal = () => {
    setShowMypriceModal(false);
  };
  const handleCloseModal = () => {
    clickEvent({
      clickParams: {
        event_name: 'myprice_modal',
        event_context: 'popup_close',
      },
      routeType: RouteType.OSMAP,
      commonData,
      localization,
      curiosityParams,
    });
    closeModal();
  };

  const handleConfirm = () => {
    const queryParams = getSearchParams();

    clickEvent({
      clickParams: {
        event_name: 'myprice_modal',
        event_context: 'popup_confirm',
      },
      routeType: RouteType.OSMAP,
      commonData,
      localization,
      curiosityParams,
    });

    starTrack.myPriceLoginPopupClicked(createMyPriceLoginPopupClickedPayload({ searchParams: queryParams }));

    window.dispatchEvent(new CustomEvent('lmn:login:modal:open'));
    closeModal();
  };

  return (
    <OpenXModal
      isOpen={showMypriceModal}
      onClose={handleCloseModal}
      brand={localization.brand}
      className="myprice-modal"
      size={MODAL_SIZES.S}
      id="myprice_modal"
      aria-label={t('vacation.oss.filters.myprice.title')}
      isMobile={isMobile}
      style={{ textAlign: 'center' }}
      header={{
        sticky: false,
      }}
      footer={{
        sticky: true,
        element: (
          <ModalButtons>
            <ModalPrimaryButton onClick={handleConfirm}>{primary}</ModalPrimaryButton>
            <ModalSecondaryButton onClick={handleCloseModal}>{secondary}</ModalSecondaryButton>
          </ModalButtons>
        ),
      }}
    >
      <ModalImage src={image} alt="" />
      <ModalBody dangerouslySetInnerHTML={{ __html: description }} />
    </OpenXModal>
  );
};

export default MyPriceModal;
